import * as React from 'react';
import { clearStoredSession, readStoredSession, writeStoredSession } from './session-storage';
import type { SessionUser } from './session-types';
import { apiClient } from '../lib/api-client';

const INACTIVITY_MS = 30 * 60 * 1000;

type AuthStatus = 'loading' | 'ready';

interface AuthContextValue {
  user: SessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (correo: string, contrasena: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [user, setUser] = React.useState<SessionUser | null>(null);
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const activityTimer = React.useRef<number | undefined>(undefined);

  const clearSession = React.useCallback(() => {
    clearStoredSession();
    apiClient.setAccessToken(null);
    setUser(null);
  }, []);

  const resetActivityTimer = React.useCallback(() => {
    if (activityTimer.current !== undefined) {
      window.clearTimeout(activityTimer.current);
    }
    if (user) {
      activityTimer.current = window.setTimeout(clearSession, INACTIVITY_MS);
    }
  }, [clearSession, user]);

  React.useEffect(() => {
    const storedSession = readStoredSession();
    if (!storedSession) {
      setStatus('ready');
      return;
    }

    apiClient.setAccessToken(storedSession.accessToken);
    apiClient.me()
      .then((currentUser) => {
        setUser({ ...storedSession.user, ...currentUser });
      })
      .catch(() => {
        clearSession();
      })
      .finally(() => setStatus('ready'));
  }, [clearSession]);

  React.useEffect(() => {
    apiClient.setUnauthorizedHandler(clearSession);
    return () => apiClient.setUnauthorizedHandler(undefined);
  }, [clearSession]);

  React.useEffect(() => {
    if (!user) {
      return undefined;
    }
    const activityEvents = ['pointerdown', 'keydown', 'touchstart'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetActivityTimer));
    resetActivityTimer();
    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetActivityTimer));
      if (activityTimer.current !== undefined) {
        window.clearTimeout(activityTimer.current);
      }
    };
  }, [resetActivityTimer, user]);

  const login = React.useCallback(async (correo: string, contrasena: string) => {
    const response = await apiClient.login(correo, contrasena);
    writeStoredSession(response);
    apiClient.setAccessToken(response.accessToken);
    setUser(response.user);
  }, []);

  const logout = React.useCallback(async () => {
    try {
      if (user) {
        await apiClient.logout();
      }
    } finally {
      clearSession();
    }
  }, [clearSession, user]);

  const value = React.useMemo<AuthContextValue>(() => ({
    user,
    isAuthenticated: user !== null,
    isLoading: status === 'loading',
    login,
    logout,
  }), [login, logout, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe utilizarse dentro de AuthProvider');
  }
  return context;
}

export { INACTIVITY_MS };
